import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Save, RefreshCw, UploadCloud, Image as ImageIcon } from "lucide-react";

const COLORS = [
  { label: "Merah", value: "text-red-500" },
  { label: "Biru", value: "text-blue-500" },
  { label: "Biru Tua", value: "text-blue-600" },
  { label: "Hijau", value: "text-green-500" },
  { label: "Cyan", value: "text-cyan-400" },
  { label: "Kuning", value: "text-yellow-400" },
  { label: "Putih", value: "text-white" },
];

// Edge flood-fill background removal: only removes the background connected to
// the image borders, so white/light pixels inside the logo are preserved.
const stripBackground = (ctx, w, h) => {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  // sample background color from the 4 corners (median-ish average)
  const corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + (w - 1)) * 4];
  let br = 0, bg = 0, bb = 0;
  corners.forEach((i) => { br += d[i]; bg += d[i + 1]; bb += d[i + 2]; });
  br /= 4; bg /= 4; bb /= 4;
  const TOL = 42; // color distance tolerance
  const near = (i) => {
    if (d[i + 3] === 0) return true;
    const dr = d[i] - br, dg = d[i + 1] - bg, db = d[i + 2] - bb;
    return Math.sqrt(dr * dr + dg * dg + db * db) <= TOL;
  };
  const visited = new Uint8Array(w * h);
  const stack = [];
  const push = (x, y) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const p = y * w + x; if (visited[p]) return;
    visited[p] = 1; stack.push(p);
  };
  for (let x = 0; x < w; x++) { push(x, 0); push(x, h - 1); }
  for (let y = 0; y < h; y++) { push(0, y); push(w - 1, y); }
  while (stack.length) {
    const p = stack.pop();
    const i = p * 4;
    if (!near(i)) continue;
    d[i + 3] = 0; // make transparent
    const x = p % w, y = (p / w) | 0;
    push(x + 1, y); push(x - 1, y); push(x, y + 1); push(x, y - 1);
  }
  ctx.putImageData(img, 0, 0);
};

const fileToPng = (file, maxDim = 240, removeBg = false) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new window.Image();
  img.onload = () => {
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);
    try {
      if (removeBg) { try { stripBackground(ctx, w, h); } catch (e) { /* keep original on failure */ } }
      resolve(c.toDataURL("image/png"));
    } catch (err) { reject(err); }
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
  img.src = url;
});

export default function AdminBrands() {
  const { isAdmin, loading, settings, updateSettings } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [siteLogo, setSiteLogo] = useState("");
  useEffect(() => { setSiteLogo(settings?.site_logo || ""); }, [settings]);
  const onSiteLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { setSiteLogo(await fileToPng(f, 320)); }
    catch { toast.error("Format gambar tidak didukung browser (coba PNG/JPG)."); }
    e.target.value = "";
  };
  const saveSiteLogo = async () => {
    setBusy(true);
    try { await updateSettings({ site_logo: siteLogo }); toast.success("Logo web tersimpan — cek header & footer"); }
    catch { toast.error("Gagal menyimpan logo web"); }
    finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/brands"); setItems(r.data?.items || []); }
    catch { toast.error("Gagal memuat"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const upd = (i, patch) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const addBrand = () => setItems((p) => [...p, { name: "BRAND", count: "0+ ECUs", color: "text-cyan-400", logo: "" }]);

  const onFile = async (i, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { upd(i, { logo: await fileToPng(file, 240, true) }); toast.success("Background dihapus otomatis"); }
    catch { toast.error("Format gambar tidak didukung browser (coba PNG/JPG)."); }
    e.target.value = "";
  };

  const removeBgExisting = async (i) => {
    const src = items[i]?.logo; if (!src) return;
    try {
      const out = await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
          const c = document.createElement("canvas"); c.width = img.width; c.height = img.height;
          const ctx = c.getContext("2d"); ctx.drawImage(img, 0, 0);
          try { stripBackground(ctx, img.width, img.height); resolve(c.toDataURL("image/png")); } catch (err) { reject(err); }
        };
        img.onerror = () => reject(new Error("decode"));
        img.src = src;
      });
      upd(i, { logo: out }); toast.success("Background dihapus — jangan lupa Simpan Semua");
    } catch { toast.error("Gagal menghapus background"); }
  };

  const save = async () => {
    setBusy(true);
    try { await api.put("/brands", { items }); toast.success("Logo & brand tersimpan — cek halaman Home"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  if (!loading && !isAdmin) return <div className="p-10 text-center text-slate-500" data-testid="brands-denied">Khusus admin.</div>;

  return (
    <div className="p-4 sm:p-8 max-w-[1000px] mx-auto" data-testid="admin-brands-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Admin</div>
          <h1 className="font-head font-black text-4xl uppercase tracking-tight mt-1">Brand Logo</h1>
          <p className="text-slate-500 text-sm mt-1">Upload logo tiap merek — background otomatis dihapus (transparan). Gunakan "Hapus BG" untuk logo yang sudah ada. Kosong = tampil nama merek berwarna.</p>
        </div>
        <Button data-testid="brands-refresh" onClick={load} disabled={busy} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800"><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>

      <div data-testid="site-logo-card" className="rounded-2xl border border-cyan-400/30 bg-[#0c1017] p-5 mb-5">
        <h2 className="font-head font-bold text-xl uppercase text-white mb-1">Logo Web (Header &amp; Footer)</h2>
        <p className="text-xs text-slate-500 mb-3">Upload logo situs (PNG transparan disarankan). Kosong = pakai ikon default (bolt + TRMD).</p>
        <div className="flex flex-wrap items-center gap-4">
          <div className="h-16 w-44 rounded-lg bg-[#07090e] border border-white/10 grid place-items-center overflow-hidden">
            {siteLogo ? <img src={siteLogo} alt="logo web" className="max-h-12 w-auto object-contain" /> : <span className="text-slate-600 text-xs">Default</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-100"><UploadCloud className="h-4 w-4" /> Upload Logo<input data-testid="site-logo-upload" type="file" accept="image/*" onChange={onSiteLogo} className="hidden" /></label>
            {siteLogo && <Button data-testid="site-logo-clear" onClick={() => setSiteLogo("")} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">Hapus</Button>}
            <Button data-testid="site-logo-save" onClick={saveSiteLogo} disabled={busy} className="bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-950 font-bold"><Save className="h-4 w-4 mr-1.5" /> Simpan Logo Web</Button>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((b, i) => (
          <div key={i} data-testid={`brand-edit-${i}`} className="rounded-2xl border border-cyan-400/30 bg-[#0c1017] p-5">
            <div className="h-28 rounded-xl bg-[#07090e] border border-white/10 grid place-items-center mb-3 overflow-hidden">
              {b.logo ? <img src={b.logo} alt={b.name} className="max-h-20 w-auto object-contain" /> : <span className={`font-head font-black text-2xl italic ${b.color}`}>{b.name}</span>}
            </div>
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Nama</label>
            <input data-testid={`brand-name-${i}`} value={b.name} onChange={(e) => upd(i, { name: e.target.value })} className="w-full mt-1 mb-2 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-400/60" />
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Jumlah ECU</label>
            <input data-testid={`brand-count-${i}`} value={b.count} onChange={(e) => upd(i, { count: e.target.value })} className="w-full mt-1 mb-2 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-400/60" />
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Warna nama (fallback)</label>
            <select data-testid={`brand-color-${i}`} value={b.color} onChange={(e) => upd(i, { color: e.target.value })} className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-400/60">
              {COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-100">
                <UploadCloud className="h-4 w-4" /> Upload Logo
                <input data-testid={`brand-upload-${i}`} type="file" accept="image/*" onChange={(e) => onFile(i, e)} className="hidden" />
              </label>
              {b.logo && <Button data-testid={`brand-removebg-${i}`} onClick={() => removeBgExisting(i)} variant="outline" className="border-cyan-700 text-cyan-300 hover:bg-cyan-950">Hapus BG</Button>}
              {b.logo && <Button data-testid={`brand-clear-${i}`} onClick={() => upd(i, { logo: "" })} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">Hapus Logo</Button>}
              <Button data-testid={`brand-remove-${i}`} onClick={() => remove(i)} variant="outline" className="border-red-800 text-red-400 hover:bg-red-950">Hapus Kartu</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mt-5">
        <Button data-testid="brands-save" onClick={save} disabled={busy} className="bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-950 font-bold"><Save className="h-4 w-4 mr-1.5" /> Simpan Semua</Button>
        <Button data-testid="brands-add" onClick={addBrand} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">+ Tambah Merek</Button>
      </div>
    </div>
  );
}
